import React, { CSSProperties, useEffect } from 'react';
import type { ApiService } from '@admin-console/shared';
import { useAutoSignOut } from '../hooks/useAutoSignOut';
import { useTokenKeepAlive } from '../hooks/useTokenKeepAlive';

interface Props {
  onSignOut: () => void;
  apiService: ApiService;
}

/**
 * Invisible sentinel that tracks user idle time.
 * When the configured timeout is reached it displays a countdown modal
 * and automatically signs the user out when the grace period expires.
 *
 * Mount this component once inside the authenticated section of the app.
 */
export const AutoSignOutManager: React.FC<Props> = ({ onSignOut, apiService }) => {
  // Active users get their JWT refreshed in the background; confirming "Stay Signed In" (or
  // dismissing the warning) also refreshes it right away.
  const refreshNow = useTokenKeepAlive(apiService);
  const { showWarning, countdown, dismiss, signOutNow } = useAutoSignOut(onSignOut, refreshNow);

  // Escape dismisses the popup, which keeps the session alive (same as Stay Signed In).
  useEffect(() => {
    if (!showWarning) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismiss();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showWarning, dismiss]);

  if (!showWarning) return null;

  const mins = Math.floor(countdown / 60);
  const secs = countdown % 60;
  const timeStr =
    mins > 0
      ? `${mins}:${String(secs).padStart(2, '0')}`
      : `${secs} second${secs !== 1 ? 's' : ''}`;

  const urgency = countdown <= 30; // turn extra-visible in the last 30 s

  return (
    <div style={S.backdrop} onClick={dismiss}>
      <div style={S.card} onClick={(event) => event.stopPropagation()}>

        {/* Header */}
        <div style={S.titleRow}>
          <span style={S.headerIcon}>⏱️</span>
          <span style={S.title}>Session Expiring Soon</span>
        </div>

        {/* Body */}
        <p style={S.message}>
          You've been inactive. To keep your session secure, you'll be
          automatically signed out in:
        </p>

        {/* Countdown display */}
        <div style={{ ...S.countdownBox, ...(urgency ? S.countdownBoxUrgent : {}) }}>
          <span style={{ ...S.countdownText, ...(urgency ? S.countdownTextUrgent : {}) }}>
            {timeStr}
          </span>
        </div>

        <p style={S.subMessage}>
          Click <strong>Stay Signed In</strong> to continue your session.
        </p>

        {/* Actions */}
        <div style={S.buttons}>
          <button style={S.btnPrimary} onClick={dismiss}>
            Stay Signed In
          </button>
          <button style={S.btnSecondary} onClick={signOutNow}>
            Sign Out Now
          </button>
        </div>

      </div>
    </div>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────

const S: Record<string, CSSProperties> = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 99999,
    padding: 24,
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 14,
    padding: '28px 28px 24px',
    maxWidth: 440,
    width: '100%',
    boxShadow: '0 12px 40px rgba(0,0,0,0.28)',
    animation: 'fadeIn 0.2s ease',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 14,
  },
  headerIcon: { fontSize: 26 },
  title: {
    fontSize: 19,
    fontWeight: 700,
    color: '#1a1a2e',
  },
  message: {
    fontSize: 14,
    color: '#555',
    lineHeight: 1.55,
    margin: '0 0 18px',
  },
  countdownBox: {
    backgroundColor: '#fff8e1',
    border: '2px solid #ffc107',
    borderRadius: 10,
    padding: '14px 16px',
    textAlign: 'center',
    marginBottom: 16,
    transition: 'background-color 0.3s, border-color 0.3s',
  },
  countdownBoxUrgent: {
    backgroundColor: '#fdecea',
    borderColor: '#e74c3c',
  },
  countdownText: {
    fontSize: 34,
    fontWeight: 800,
    color: '#856404',
    letterSpacing: 1,
    fontVariantNumeric: 'tabular-nums',
    transition: 'color 0.3s',
  },
  countdownTextUrgent: {
    color: '#c0392b',
  },
  subMessage: {
    fontSize: 13,
    color: '#888',
    margin: '0 0 22px',
    textAlign: 'center',
  },
  buttons: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  btnPrimary: {
    padding: '13px 20px',
    borderRadius: 8,
    border: 'none',
    backgroundColor: '#3498db',
    color: '#ffffff',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    textAlign: 'center',
  },
  btnSecondary: {
    padding: '11px 20px',
    borderRadius: 8,
    border: '1px solid #d0d0d0',
    backgroundColor: 'transparent',
    color: '#888',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    textAlign: 'center',
  },
};
